import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authenticationInProgress, setAuthenticationInProgress] = useState(false);

  // Check if user is authenticated on app load
  useEffect(() => {
    const checkAuth = async () => {
      try {
        setAuthenticationInProgress(true);
        
        // Check for token in URL (from OAuth callback)
        const urlParams = new URLSearchParams(window.location.search);
        const tokenFromUrl = urlParams.get('token');
        
        if (tokenFromUrl) {
          console.log('🔄 AuthContext: Token found in URL, storing and processing...');
          localStorage.setItem('auth_token', tokenFromUrl);
          // Clean up URL
          window.history.replaceState({}, document.title, window.location.pathname);
        }
        
        const token = localStorage.getItem('auth_token');
        if (!token) {
          console.log('⚠️ AuthContext: No token found, user not authenticated');
          setLoading(false);
          setAuthenticationInProgress(false);
          return;
        }

        console.log('🔄 AuthContext: Token found, verifying with backend...');

        // Verify token with backend - with retry logic for production
        const verifyToken = async (retryCount = 0) => {
          try {
            // Production-specific timeout configuration
            const timeoutMs = process.env.NODE_ENV === 'production' ? 15000 : 5000;
            
            console.log(`🔄 AuthContext: Verifying token (attempt ${retryCount + 1})`);
            
            const response = await axios.get('/api/user/profile', {
              timeout: timeoutMs,
              headers: {
                'Authorization': `Bearer ${token}`
              }
            });
            
            console.log('✅ AuthContext: Token verification successful', response.data);
            setUser(response.data);
            setAuthenticationInProgress(false);
            
          } catch (error) {
            console.error(`❌ AuthContext: Token verification failed (attempt ${retryCount + 1}):`, error);
            
            // Retry logic for production
            if (retryCount < 2 && process.env.NODE_ENV === 'production') {
              console.log(`🔄 AuthContext: Retrying token verification in ${(retryCount + 1) * 2}s...`);
              await new Promise(resolve => setTimeout(resolve, (retryCount + 1) * 2000));
              return verifyToken(retryCount + 1);
            }
            
            // Only remove token if it's actually invalid (not just a network error)
            if (error.response?.status === 401) {
              console.log('🗑️ AuthContext: Token is invalid, removing from localStorage');
              localStorage.removeItem('auth_token');
            } else {
              console.log('⚠️ AuthContext: Network error, keeping token for now');
            }
            
            throw error;
          }
        };

        await verifyToken();
        
      } catch (error) {
        console.error('🔴 AuthContext: Final auth check failed:', error);
        // Don't remove token on network errors in production
        if (error.response?.status === 401) {
          localStorage.removeItem('auth_token');
        }
        setAuthenticationInProgress(false);
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, []);

  // Login with Google (simplified - now handled in LoginPage directly)
  const loginWithGoogle = () => {
    console.log('🔵 AuthContext: loginWithGoogle called (deprecated - should use direct redirect)');
    window.location.href = `${window.location.origin}/api/auth/google`;
  };
  
  // Login with Email/Password
  const loginWithEmail = async (email, password) => {
    try {
      console.log('🔵 AuthContext: Email login attempt');
      
      const response = await axios.post('/api/auth/login', {
        email,
        password
      });
      
      const { token, user } = response.data;
      
      // Store token
      localStorage.setItem('auth_token', token);
      
      // Set user data
      setUser(user);
      
      console.log('✅ AuthContext: Email login successful');

      // Redirect to agency dashboard
      window.location.href = '/agency-dashboard';
      
    } catch (error) {
      console.error('❌ AuthContext: Email login failed:', error);
      
      // Extract error message
      let errorMessage = 'Anmeldung fehlgeschlagen';
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      throw new Error(errorMessage);
    }
  };

  // 🔒 SECURE: Logout - clear ALL localStorage to prevent data leakage
  const logout = () => {
    console.log('🔒 SECURITY: Clearing all localStorage on logout');
    
    // Remove authentication data
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user_id');
    
    // 🔒 CRITICAL: Remove business data to prevent showing to next user
    localStorage.removeItem('user_businesses');
    localStorage.removeItem('selected_business');
    
    // Remove any other cached data that could leak between users
    localStorage.removeItem('business_profiles');
    localStorage.removeItem('cached_locations');
    localStorage.removeItem('user_data');
    
    // Clear state
    setUser(null);
    
    console.log('🔒 SECURITY: All localStorage cleared, redirecting to login');
    window.location.href = '/login';
  };

  // Update user data
  const updateUser = (userData) => {
    setUser(prev => ({ ...prev, ...userData }));
  };

  // Check if user's trial has expired (only for regular users)
  const isTrialExpired = () => {
    if (!user || user.userType === 'subuser') return false;
    if (user.subscription?.status !== 'trial') return false;
    return new Date() > new Date(user.subscription?.trialEndDate);
  };

  // Check if user has active subscription (only for regular users)
  const hasActiveSubscription = () => {
    if (!user || user.userType === 'subuser') return true; // Sub-users inherit team owner's subscription
    return user.subscription?.status === 'active' && 
           user.subscription?.subscriptionEndDate && 
           new Date() < new Date(user.subscription?.subscriptionEndDate);
  };

  // Check if user can use premium features
  const canUsePremiumFeatures = () => {
    if (user?.userType === 'subuser') return true; // Sub-users inherit team owner's features
    return !isTrialExpired() || hasActiveSubscription();
  };

  // Get remaining trial days (only for regular users)
  const getRemainingTrialDays = () => {
    if (!user || user.userType === 'subuser') return 0;
    if (user.subscription?.status !== 'trial') return 0;
    const trialEnd = new Date(user.subscription?.trialEndDate);
    const now = new Date();
    const diffTime = trialEnd - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(0, diffDays);
  };

  // ===== ROLE-BASED ACCESS CONTROL =====

  // Get user role (works for both User and SubUser)
  const getUserRole = () => {
    return user?.role || null;
  };

  // Check if user has specific role
  const hasRole = (role) => {
    return getUserRole() === role;
  };

  // Check if user has any of the specified roles
  const hasAnyRole = (roles) => {
    const userRole = getUserRole();
    return roles.includes(userRole);
  };

  // Check if user is admin
  const isAdmin = () => {
    return hasRole('admin');
  };

  // Check if user can manage content (admin or member)
  const canManageContent = () => {
    return hasAnyRole(['admin', 'member']);
  };

  // Check if user can view content (any authenticated role)
  const canViewContent = () => {
    return ['admin', 'member', 'viewer'].includes(getUserRole());
  };

  // Check if user can manage users (admin only)
  const canManageUsers = () => {
    return isAdmin();
  };

  // Check if user is read-only (viewer role)
  const isReadOnly = () => {
    return hasRole('viewer');
  };

  // Check specific permission
  const hasPermission = (action) => {
    const role = getUserRole();
    const permissions = {
      'read': ['admin', 'member', 'viewer'].includes(role),
      'write': ['admin', 'member'].includes(role),
      'delete': ['admin', 'member'].includes(role),
      'manage_users': role === 'admin',
      'access_settings': role === 'admin',
      'manage_team': role === 'admin'
    };
    return permissions[action] || false;
  };

  // Get role label for display
  const getRoleLabel = () => {
    const labels = {
      admin: 'Administrator',
      member: 'Marketing Manager',
      viewer: 'Viewer'
    };
    return labels[getUserRole()] || 'Unknown';
  };

  const value = {
    user,
    loading,
    authenticationInProgress,
    loginWithGoogle,
    loginWithEmail,
    logout,
    updateUser,
    isTrialExpired,
    hasActiveSubscription,
    canUsePremiumFeatures,
    getRemainingTrialDays,
    // Role-based access control
    getUserRole,
    hasRole,
    hasAnyRole,
    isAdmin,
    canManageContent,
    canViewContent,
    canManageUsers,
    isReadOnly,
    hasPermission,
    getRoleLabel
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};